import {AceLoggerService} from '@logging/ace-logger.service';
import {ObjectUtils} from '@app/shared/util/general/object-utils';
import {Equipment, EquipmentId, Favourite, FavouriteId} from '@app/graphql-typings/graphql';
import {SiteService} from '@shared/services';
import {AsyncSubject, forkJoin, from, Observable, of} from 'rxjs';
import {concatMap, first, map, mergeMap, take} from 'rxjs/operators';
import {Injectable, Optional} from '@angular/core';
import moment, {Moment} from 'moment-timezone';
import {ParamMap} from '@angular/router';
import {EquipmentGroup, FunctionParamsAndChartRawValues} from '@app/layout/charts/chart-controls/equipment-group/equipment-group.component';
import {ConditionOperator, DrawChartEvent, FilterCondition} from '@app/layout/charts/chart.service';
import {TimePeriod} from '@app/layout/charts/chart-functions/time-period';
import {ReferenceLine} from '@app/layout/charts/chart-controls/reference-line';
import {AceLogger} from '@logging/ace-logger';
import {AGGREGATION_LABELS_BY_FUNCTION_NAME, FunctionName, FunctionParams} from '@app/layout/charts/chart-functions/chart-function-types';
import {SiteId, SiteInfo} from '@shared/services/api/access-sites.interface';
import {EquipmentService} from '@shared/services/api/equipment.service';
import {EquipmentCache, EquipmentCacheImpl} from '@shared/data-stores/equipment-cache';
import {FavouritesCache, FavouritesCacheImpl} from '@shared/data-stores/favourites-cache';
import {CHART_TASK_SCOPE_OPTIONS, TasksCache, TasksCacheImpl} from '@shared/data-stores/tasks-cache';
import {CacheById} from '@shared/data-stores/cache-by-id';
import {CollectionUtils} from '@shared/util/collections/collection-utils';
import {FavouriteScopeOptions, FavouritesService} from "@app/layout/sitemanager/collectors-favourites/favourites.service";
import {RulesService} from "@app/layout/rules/rulesmanager/rules.service";
import {Task, TaskId} from "@shared/services/api/task.interface";
import {JS_CHART_SERIES_TYPE} from "@app/layout/charts/mini-chart/mini-chart.component";
import {DateTimeRangePickerComponent} from "@shared/components/date-time-range-picker/date-time-range-picker.component";
import {CHART_RELATIVE_DATE_PARAM_NAMES} from "@app/layout/charts/chart-url-translator.service";
import {DateSelectComponentFields} from "@shared/components/date-time-range-select/date-time-range-select.component";
import {BrowserUtils} from "@shared/util/general/browser-utils";
import {ChartsUtil} from "@app/layout/charts/charts-util";
import {ScheduleUtil} from "@app/layout/charts/schedule-util";
import {AppConstants} from '@app/shared/util/app-constants';
import {EnumUtils} from '@app/shared/util/general/enum-utils';
import {StringUtils} from '@app/shared/util/general/string-utils';

export interface DrawChartEventCache {
  site: SiteInfo;
}

export function createDrawChartEventCache(): DrawChartEventCache {
  return {
    site: null
  };
}

interface EquipmentIdGroup {
  equipmentId: EquipmentId;
  favouriteIds: FavouriteId[];
  taskIds: TaskId[];
  functionParamsAndChartRawValues?: FunctionParamsAndChartRawValues;
}

export class NoSiteIdError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NoSiteIdError';
  }
}

/** `parseOldUrl` needs equipment in favourites fetch because the old url doesn't have equipment ids.
 * This is the scope that will make the fetch include equipment property when it retrieves favourites. */
const OLD_URL_FAVOURITE_SCOPE_OPTIONS: FavouriteScopeOptions = {getEquipment: true, getDeviceObject: true};

@Injectable()
export class SharedEquipmentService {

  private readonly SEPARATOR = '-';
  readonly X_AXIS_FAVOURITE_URL_PARAM_PREFIX = 'xaxisfav' + this.SEPARATOR;
  readonly X_AXIS_BIN_SIZE_URL_PARAM = 'xaxisbinsize';
  readonly AGGREGATION_URL_PARAM = 'aggregatefunction';
  private readonly FILTER_URL_PARAM_PREFIX = 'filter-';
  readonly METADATA_CONDITION_METADATA_URL_PARAM = this.FILTER_URL_PARAM_PREFIX + 'metadata';
  readonly METADATA_CONDITION_OPERATOR_URL_PARAM = this.FILTER_URL_PARAM_PREFIX + 'operator';
  readonly METADATA_CONDITION_VALUE_URL_PARAM = this.FILTER_URL_PARAM_PREFIX + 'conditionvalue';
  readonly WORKING_HOURS_FILTER_URL_PARAM = this.FILTER_URL_PARAM_PREFIX + 'workinghours';
  private readonly FAVOURITE_URL_PARAM_PREFIX = 'fav-';
  private readonly TASK_URL_PARAM_PREFIX = 'task-';
  private readonly logger: AceLogger;

  private readonly equipmentCache: EquipmentCache;
  private readonly favouritesCache: FavouritesCache;
  private readonly tasksCache: TasksCache;
  private readonly siteIdByEquipmentIdCache: CacheById<EquipmentId, SiteId | null>;

  constructor(private siteService: SiteService,
              rulesResolverService: RulesService,
              equipmentService: EquipmentService,
              favouritesService: FavouritesService,
              @Optional() equipmentCache: EquipmentCache,
              @Optional() favouritesCache: FavouritesCache,
              @Optional() tasksCache: TasksCache,
              logService: AceLoggerService) {
    this.logger = logService.create(SharedEquipmentService.name);
    this.equipmentCache = equipmentCache ?? new EquipmentCacheImpl(equipmentService, false);
    this.favouritesCache = favouritesCache ?? new FavouritesCacheImpl(favouritesService, OLD_URL_FAVOURITE_SCOPE_OPTIONS, false);
    this.tasksCache = tasksCache ?? new TasksCacheImpl(rulesResolverService, CHART_TASK_SCOPE_OPTIONS, false);
    this.siteIdByEquipmentIdCache = new CacheById<EquipmentId, SiteId>(
      (equipmentIds: EquipmentId[]) => equipmentService.getEquipmentByIds(equipmentIds, {scopeOptions: {getCollectorData: true}}).pipe(
        map(equipment => CollectionUtils.indexByFn(
          equipment,
          equipmentItem => equipmentItem.equipment_id,
          equipmentItem => equipmentItem.collector?.site_id ?? null
        ))
      )
    );
  }

  observable: AsyncSubject<any>;

  static getNonSharedFavourites(favourites: Favourite[]): Favourite[] {
    return favourites.filter(f => {
      return f.equipment_ids.length === 1 && f.equipment_ids[0] === f.canonical_equipment_id;
    });
  }

  static getSharedFavourites(favourites: Favourite[]): Favourite[] {
    return favourites.filter(f => {
      return f.equipment_ids.length > 1 || (f.equipment_ids.length === 1 && f.equipment_ids[0] !== f.canonical_equipment_id);
    });
  }

  getRemainingSharedFavourites(sharedFavourites: Favourite[], replacedSharedFavs: Favourite[]) {
    return sharedFavourites.filter(f => {
      return replacedSharedFavs.indexOf(f) === -1;
    });
  }

  replaceSharedEquipmentWithRelated(sharedFavourites, nonSharedFavourites): Favourite[] {
    const replacedSharedFavs: Favourite[] = [];

    // for each favourite with shared equipment
    sharedFavourites.forEach(currentSharedFav => {

      // for each equipment in the equipment_ids field of the shared equipment
      currentSharedFav.equipment_ids.forEach(sharedFavsEquipmentId => {

        // for each nonshared favourite
        nonSharedFavourites.forEach(currentNonSharedFav => {

          // if we find a related equipment
          if (sharedFavsEquipmentId === currentNonSharedFav.canonical_equipment_id) {

            // set shared favourites equipment to the related equipment
            currentSharedFav.equipment = currentNonSharedFav.equipment;
            replacedSharedFavs.push(currentSharedFav);
          }
        });
      });
    });
    return replacedSharedFavs;
  }

  // if more than 1 remaining favourite with shared equipment - check if there's a common equipment id
  findCommonEquipment(unreplacedSharedFavourites: Favourite[]): EquipmentId[] {
    let commonEquipmentIds: EquipmentId[] = [];

    if (unreplacedSharedFavourites.length > 1) {

      // get array of equipment_ids
      const arrayOfEquipmentIdArrays: Array<EquipmentId[]> = unreplacedSharedFavourites.map(f => f.equipment_ids);

      // check if a common equipment id exists among the equipment_ids fields of all remaining favourites with shared equipment
      commonEquipmentIds = <EquipmentId[]>ObjectUtils.intersection.apply(this, arrayOfEquipmentIdArrays);
    }
    return commonEquipmentIds;
  }

  replaceSharedEquipmentWithCommonEquipment(commonEquipmentId: EquipmentId, unreplacedSharedFavourites: Favourite[]): void {
    // set all remaining favourites equipment to the common equipment
    this.equipmentCache.getEquipmentByIds(Array.of(commonEquipmentId)).subscribe(equipmentById => {
      const commonEquipment: Equipment = equipmentById.get(commonEquipmentId);
      if (commonEquipment != null) {
        unreplacedSharedFavourites.forEach(f => {
          f.equipment = commonEquipment;
        });
      }
      this.observable.next(undefined);
      this.observable.complete();
    });
  }

  replaceSharedEquipmentWithTheirFirstEquipment(unreplacedSharedFavourites: Favourite[]): void {
    // build map of favourites and their replacement equipment id
    const favouritesReplaceMap = new Map<EquipmentId, Favourite>();
    unreplacedSharedFavourites.forEach(currentFav => {
      const replacementEquipmentId = currentFav.equipment_ids[0];
      favouritesReplaceMap.set(replacementEquipmentId, currentFav);
    });

    const listOfEquipmentIds = Array.from(favouritesReplaceMap.keys());
    this.equipmentCache.getEquipmentByIds(listOfEquipmentIds).subscribe(equipmentById => {
      favouritesReplaceMap.forEach((favourite, equipmentId) => {
        const equipment = equipmentById.get(equipmentId);
        if (equipment != null) {
          favourite.equipment = equipment;
        }
      });
      this.observable.next(undefined);
      this.observable.complete();
    });
  }

  updateSharedFavourites(favourites: Favourite[]): AsyncSubject<any> {
    this.observable = new AsyncSubject();
    this.logger.debug('updateSharedFavourites() before favourites\n', JSON.stringify(favourites));

    const sharedFavourites = SharedEquipmentService.getSharedFavourites(favourites);
    this.logger.debug('updateSharedFavourites() sharedFavourites', sharedFavourites);

    if (sharedFavourites.length > 0) {

      const nonSharedFavourites = SharedEquipmentService.getNonSharedFavourites(favourites);
      this.logger.debug('updateSharedFavourites() nonSharedFavourites', nonSharedFavourites);

      const replacedSharedFavs: Favourite[] = this.replaceSharedEquipmentWithRelated(sharedFavourites, nonSharedFavourites);
      this.logger.debug('updateSharedFavourites() replacedSharedFavs', replacedSharedFavs);

      const remainingSharedFavourites = this.getRemainingSharedFavourites(sharedFavourites, replacedSharedFavs);

      if (remainingSharedFavourites.length > 0) {

        const commonEquipmentIds: EquipmentId[] = this.findCommonEquipment(remainingSharedFavourites);
        this.logger.debug('updateSharedFavourites() commonEquipmentIds', commonEquipmentIds);

        if (commonEquipmentIds.length > 0) {
          this.replaceSharedEquipmentWithCommonEquipment(commonEquipmentIds[0], remainingSharedFavourites);
        } else {
          this.replaceSharedEquipmentWithTheirFirstEquipment(remainingSharedFavourites);
        }

        setTimeout(() => {
          this.logger.debug('updateSharedFavourites() after favourites\n', JSON.stringify(favourites));
        }, 1000);

      } else {
        this.observable.next(undefined);
        this.observable.complete();
      }

    } else {
      this.observable.next(undefined);
      this.observable.complete();
    }
    return this.observable;
  }

  recordEquipmentSite(siteId: SiteId, equipment: Equipment): void {
    this.siteIdByEquipmentIdCache.setItems(new Map([[equipment.equipment_id, of(siteId)]]));
  }

  parseUrl(queryParamMap: ParamMap, cache: DrawChartEventCache): Observable<{hasPermission: boolean, event: DrawChartEvent}> {

    if (!cache) {
      cache = createDrawChartEventCache();
    }

    this.logger.debug('parseNewUrl()');

    const relativeDateTimeRange = DateTimeRangePickerComponent.getDateTimeRangeFromParams(queryParamMap, CHART_RELATIVE_DATE_PARAM_NAMES, AppConstants.UTC_TIMEZONE);
    const split = queryParamMap.get('split') ? queryParamMap.get('split') === 'true' : false;
    const splitByUnit = queryParamMap.get('splitbyunit') ? queryParamMap.get('splitbyunit') === 'true' : false;
    const splitByEquipment = queryParamMap.get('splitbyequip') ? queryParamMap.get('splitbyequip') === 'true' : false;
    const chartType = queryParamMap.get('chartType') ? ObjectUtils.getEnumValueFromString(JS_CHART_SERIES_TYPE, queryParamMap.get('chartType')) : null;
    const referenceLinesStrings: string[] = queryParamMap.getAll('referenceLines');
    const workingHours: boolean = queryParamMap.get('workinghours') ? queryParamMap.get('workinghours') === 'true' : false;
    const timezone: string = queryParamMap.get('timezone') || null;
    const xAxisIds = this.getXAxisEquipmentAndFavouriteId(queryParamMap);
    const xAxisBinSize = StringUtils.stringToDecimal(queryParamMap.get(this.X_AXIS_BIN_SIZE_URL_PARAM));
    const aggregationFunctionParam = queryParamMap.get(this.AGGREGATION_URL_PARAM);
    const functionName = aggregationFunctionParam != null
      ? EnumUtils.parseEnum(FunctionName, aggregationFunctionParam)
      : null;
    const aggregationFunction = functionName != null && AGGREGATION_LABELS_BY_FUNCTION_NAME.has(functionName)
      ? functionName
      : null;
    const isXAxisMode = xAxisIds != null;

    if (!relativeDateTimeRange.relativeDate) {
      const startDate = relativeDateTimeRange[DateSelectComponentFields.START_DATE];
      const endDate = relativeDateTimeRange[DateSelectComponentFields.END_DATE];
      if (!startDate || !moment.utc(startDate).isValid()) {
        throw new Error('missing or invalid start param');
      } else if (!endDate || !moment.utc(endDate).isValid()) {
        throw new Error('missing or invalid end param');
      } else if (startDate.isSameOrAfter(endDate)) {
        throw new Error('Start date is after end date');
      }
    }

    const equipmentIdGroups: EquipmentIdGroup[] = this.parseNewUrlIntoGroups(queryParamMap);
    this.logger.debug('parseNewUrl() equipmentGroups', equipmentIdGroups);

    const idsToFetch = {
      equipmentIds: new Set<EquipmentId>(),
      favouriteIds: new Set<FavouriteId>(),
      taskIds: new Set<TaskId>()
    };

    if (isXAxisMode) {
      if (xAxisBinSize == null || xAxisBinSize <= 0) {
        throw new Error('Missing or invalid bin size param');
      }
      if (aggregationFunction == null) {
        throw new Error('Missing or invalid aggregation function');
      }
      idsToFetch.equipmentIds.add(xAxisIds.equipmentId);
      idsToFetch.favouriteIds.add(xAxisIds.favouriteId);
    }

    equipmentIdGroups.forEach((group: EquipmentIdGroup) => {
      idsToFetch.equipmentIds.add(group.equipmentId);

      group.favouriteIds.forEach(id => idsToFetch.favouriteIds.add(id));
      group.taskIds.forEach(id => idsToFetch.taskIds.add(id));
    });

    const equipmentIds = Array.from(idsToFetch.equipmentIds);
    const taskIds = Array.from(idsToFetch.taskIds);
    let tasksById$: Observable<Map<TaskId, Task>>;
    if (isXAxisMode) {
      if (idsToFetch.taskIds.size) {
        this.logger.error('parseUrl() ignoring task ids because the x axis favourite mode does not draw tasks', taskIds);
      }
      tasksById$ = of(new Map<TaskId, Task>());
    } else {
      tasksById$ = this.tasksCache.getTasksByIds(taskIds, equipmentIds);
    }
    return forkJoin({
      equipmentAndSite: this.getEquipmentAndSite(equipmentIds),
      favourites: this.favouritesCache.getFavouritesByIds(Array.from(idsToFetch.favouriteIds), equipmentIds),
      tasks: tasksById$
    }).pipe(
      mergeMap(result => {
        const siteIdFromEquipment: SiteId | null = result?.equipmentAndSite?.siteId ?? null;
        const byId = {
          equipment: result?.equipmentAndSite?.equipmentById ?? new Map<EquipmentId, Equipment>(),
          favourites: result?.favourites ?? new Map<FavouriteId, Favourite>(),
          tasks: result?.tasks ?? new Map<TaskId, Task>()
        };

        let xAxisProperties: Partial<DrawChartEvent>;
        if (isXAxisMode) {
          const xAxisFavourite: Favourite = byId.favourites.get(xAxisIds.favouriteId);
          const xAxisEquipment = byId.equipment.get(xAxisIds.equipmentId);
          if (xAxisFavourite == null || xAxisEquipment == null) {
            throw new Error('Missing or invalid x axis favourite or equipment');
          }
          xAxisFavourite.equipment = xAxisEquipment;
          xAxisProperties = {
            xAxis: {equipment: xAxisFavourite.equipment, favourite: xAxisFavourite, binSize: xAxisBinSize},
            aggregationFunction,
            filterCondition: this.getFilterConditionFromUrl(queryParamMap)
          };
        }

        const equipmentGroups: EquipmentGroup[] = [];
        equipmentIdGroups.forEach((group: EquipmentIdGroup) => {
          const equipment: Equipment = byId.equipment.get(group.equipmentId);
          const favourites: Favourite[] = group.favouriteIds.reduce((acc, favId) => {
            const fav = byId.favourites.get(favId);
            if (fav) {
              fav.equipment = equipment;
              acc.push(fav);
            }
            return acc;
          }, []);
          const tasks: Task[] = group.taskIds.reduce((acc, taskId) => {
            const task = byId.tasks.get(taskId);
            if (task) {
              acc.push(task);
            }
            return acc;
          }, []);
          const equipmentGroup: EquipmentGroup = {
            equipment: equipment,
            favourites: favourites,
            tasks: tasks,
            functionParamsAndChartRawValues: group.functionParamsAndChartRawValues
          };
          equipmentGroups.push(equipmentGroup);
        });

        const referenceLines: ReferenceLine[] = [];
        if (referenceLinesStrings && referenceLinesStrings.length) {
          referenceLinesStrings.forEach((referenceLineHashCode: string) => {
            referenceLines.push(ReferenceLine.fromHashCode(referenceLineHashCode));
          });
        }

        let site$: Observable<SiteInfo> = null;
        if (cache.site != null && (siteIdFromEquipment == null || cache.site.site_id === siteIdFromEquipment)) {
          site$ = of(cache.site);
        } else if (siteIdFromEquipment != null) {
          site$ = this.siteService.getCachedSiteInfo(siteIdFromEquipment);
        } else {
          throw new NoSiteIdError('Cannot determine site id for DrawChartEvent');
        }

        return forkJoin({
          event: of({
            site: null,
            ...xAxisProperties,
            equipmentGroups: equipmentGroups,
            split: split,
            splitByUnit: splitByUnit,
            splitByEquipment: splitByEquipment,
            chartType: chartType,
            referenceLines: referenceLines,
            workingHours: workingHours,
            ...relativeDateTimeRange,
            ...timezone && {timezone}
          }),
          site: site$
        });
      }),
      map(({event, site}) => {
        event.site = site;
        // The url may have been edited or the site changed such that it has no working hours. In that case, check and clear.
        if (event.filterCondition?.workingHours && !ScheduleUtil.canFilterByWorkingHours(site)) {
          event.filterCondition.workingHours = false;
        }
        const timezone = site?.timezone ?? BrowserUtils.getBrowserTimezone();
        const resolvedRelativeDateTimeRange = DateTimeRangePickerComponent.resolveValueAsRange(event, timezone, false, true);
        const startDate = resolvedRelativeDateTimeRange[DateSelectComponentFields.START_DATE];
        let endDate = resolvedRelativeDateTimeRange[DateSelectComponentFields.END_DATE];
        if (event.relativeDate) {
          endDate = endDate.clone().add(ChartsUtil.CHART_END_TS_ADJUSTMENT);
        }
        return {
          hasPermission: !!site,
          event: {
            ...event,
            [DateSelectComponentFields.START_DATE]: startDate,
            [DateSelectComponentFields.END_DATE]: endDate
          }
        };
      })
    );
  }

  getXAxisEquipmentAndFavouriteId(queryParamMap: ParamMap): {equipmentId: EquipmentId, favouriteId: FavouriteId} | null {
    const xAxisFavKey = queryParamMap.keys.find(key => key.startsWith(this.X_AXIS_FAVOURITE_URL_PARAM_PREFIX));
    if (xAxisFavKey) {
      const equipmentId = Number(xAxisFavKey.split(this.SEPARATOR)[1]);
      const favouriteId = Number(queryParamMap.get(xAxisFavKey));
      if (isFinite(equipmentId) && isFinite(favouriteId)) {
        return {equipmentId, favouriteId};
      }
    }
    return null;
  }

  private getEquipmentAndSite(equipmentIds: EquipmentId[]): Observable<{equipmentById: Map<EquipmentId, Equipment>, siteId: SiteId | null}> {
    const maxTries = 5;
    // Resolve site id by getting it from the first equipment's collector. If the equipment does not exist or its
    // collector has no site, get it from the next one.
    const siteId$ = from(equipmentIds).pipe(
      take(maxTries),
      concatMap(equipmentId => this.siteIdByEquipmentIdCache.getById(equipmentId)),
      first(siteId => siteId != null, <null>null)
    );
    return siteId$.pipe(
      mergeMap(siteId => {
        // Fetch equipment using siteId if it was resolved to prewarm for whole charts page.
        const prewarm$: Observable<unknown> = siteId != null ? this.equipmentCache.getEquipmentForSite(siteId) : of(null);
        return prewarm$.pipe(
          mergeMap(() => this.equipmentCache.getEquipmentByIds(equipmentIds)),
          map(equipmentById => ({equipmentById, siteId}))
        );
      })
    );
  }

  private getFilterConditionFromUrl(queryParamMap: ParamMap): FilterCondition {
    const metadataId = StringUtils.stringToNumber(queryParamMap.get(this.METADATA_CONDITION_METADATA_URL_PARAM));
    const operator = EnumUtils.parseEnum(ConditionOperator, queryParamMap.get(this.METADATA_CONDITION_OPERATOR_URL_PARAM));
    const conditionValue = StringUtils.stringToDecimal(queryParamMap.get(this.METADATA_CONDITION_VALUE_URL_PARAM));
    const metadataCondition = (metadataId != null && operator != null && conditionValue != null)
      ? {metadataId, operator, conditionValue}
      : null;
    const workingHours: boolean = StringUtils.parseBoolean(queryParamMap.get(this.WORKING_HOURS_FILTER_URL_PARAM)) ?? false;
    return {workingHours, metadataCondition};
  }

  getEquipmentIdsFromNewUrl(queryParamMap: ParamMap): EquipmentId[] {
    const equipmentIds = queryParamMap.keys.reduce((accum, key) => {
      let equipmentId;
      if (key.startsWith(this.FAVOURITE_URL_PARAM_PREFIX)) {
        const items: string[] = key.split('-');
        equipmentId = items[1];
      } else if (key.startsWith(this.TASK_URL_PARAM_PREFIX)) {
        equipmentId = key.substring(this.TASK_URL_PARAM_PREFIX.length);
      }
      if (equipmentId) {
        accum.add(equipmentId);
      }
      return accum;
    }, new Set<string>());
    return Array.from(equipmentIds).map(e => Number(e));
  }

  parseNewUrlIntoGroups(queryParamMap: ParamMap): EquipmentIdGroup[] {
    const equipmentIds: EquipmentId[] = this.getEquipmentIdsFromNewUrl(queryParamMap);
    const equipmentIdGroups: EquipmentIdGroup[] = [];
    this.logger.debug('equipmentIds', JSON.stringify(equipmentIds));

    equipmentIds.forEach(equipmentId => {
      const favouriteKey: string = this.FAVOURITE_URL_PARAM_PREFIX + equipmentId;
      if (!queryParamMap.has(favouriteKey)) {
        throw new Error('missing or invalid fav param');
      }
      const favouriteValue: string = queryParamMap.get(favouriteKey);
      const taskValue: string = queryParamMap.get(this.TASK_URL_PARAM_PREFIX + equipmentId);
      equipmentIdGroups.push({
        equipmentId: equipmentId,
        favouriteIds: favouriteValue ? favouriteValue.split(',').reduce((acc, fav_id) => {
          if (fav_id) {
            acc.push(Number(fav_id));
          }
          return acc;
        }, [])  : [], // NOTE: Empty string is falsy
        taskIds: taskValue ? taskValue.split(',').reduce((acc, task_id) => {
          if (task_id) {
            acc.push(Number(task_id));
          }
          return acc;
        }, []) : [], // NOTE: Empty string is falsy
        functionParamsAndChartRawValues: this.getFunctionParamsFromUrl(equipmentId, queryParamMap)
      });
    });
    return equipmentIdGroups;
  }

  parseOldUrl(queryParamMap: ParamMap): Observable<DrawChartEvent> {
    /* Example of the Old URL chart format
        /charts/of/?favid[]=356482286285&favid[]=356482286286&favid[]=356482286283&taskId[]=193073-356482285596&start=2019-11-06T05:45:22.536Z&end=2021-11-13T05:45:22.536Z
    */
    this.logger.debug('parseOldUrl() queryParamMap', queryParamMap);
    const startDate: string = queryParamMap.get('start');
    const endDate: string = queryParamMap.get('end');

    if (!startDate || !moment.utc(startDate).isValid()) {
      throw new Error('missing or invalid start param');
    }

    if (!endDate || !moment.utc(endDate).isValid()) {
      throw new Error('missing or invalid end param');
    }

    let favIdKey: string = queryParamMap.keys.find(k => k.startsWith('favId'));
    if (!favIdKey) {
      favIdKey = queryParamMap.keys.find(k => k.startsWith('favid'));
    }

    let taskIdKey: string = queryParamMap.keys.find(k => k.startsWith('taskId'));
    if (!taskIdKey) {
      taskIdKey = queryParamMap.keys.find(k => k.startsWith('taskid'));
    }

    const favIdParams: string | string[] = queryParamMap.getAll(favIdKey);
    const taskIdParams: string | string[] = queryParamMap.getAll(taskIdKey);
    this.logger.debug('parseOldUrl() favIdParams', favIdParams);
    this.logger.debug('parseOldUrl() taskIdParams', taskIdParams);

    if (favIdParams === undefined || (favIdParams && favIdParams.length === 0)) {
      throw new Error('missing or invalid favId[] params');
    }

    const favIds: FavouriteId[] = this.parseFavIds(favIdParams);
    const taskIds: TaskId[] = this.parseTaskIds(taskIdParams);
    const oldTaskIds: string[] = this.parseOldTaskIds(taskIdParams);
    return forkJoin([this.favouritesCache.getFavouritesByIds(favIds), this.tasksCache.getTasksByIds(taskIds)])
      .pipe(mergeMap((details) => {

      const favourites: Favourite[] = Array.from(details[0].values());
      const tasks: Task[] = Array.from(details[1].values());

      this.logger.debug('parseOldUrl() favourites', favourites);
      this.logger.debug('parseOldUrl() tasks', tasks);

      return this.updateSharedFavourites(favourites).pipe(map(() => [favourites, tasks]));
    }), map( ([favourites, tasks]: [Favourite[], Task[]]) => {
      this.logger.debug('parseOldUrl() updated favourites no shared equipment', favourites);

      const equipment: Equipment[] = favourites.map(f => f.equipment);
      this.logger.debug('parseOldUrl() equipment', equipment);

      const equipmentToFavouriteMap: Map<EquipmentId, Favourite[]> = this.createEquipmentIdToFavouriteMap(favourites);
      this.logger.debug('parseOldUrl() equipmentToFavouriteMap', equipmentToFavouriteMap);

      const equipmentIds: EquipmentId[] = Array.from(equipmentToFavouriteMap.keys()).map(e => Number(e));
      this.logger.debug('parseOldUrl() equipmentIds', equipmentIds);

      const equipmentToTaskMap: Map<EquipmentId, Task[]> = this.createEquipmentIdToTaskMap(tasks, equipmentIds, oldTaskIds);
      this.logger.debug('parseOldUrl() equipmentToTaskMap', equipmentToTaskMap);

      const equipmentGroups: EquipmentGroup[] = [];
      equipmentIds.forEach(currentEquipmentId => {
        const currentEquipment: Equipment = equipment.find(e => e.equipment_id === currentEquipmentId);
        if (currentEquipment === undefined) {
          throw new Error('cannot find equipment for equipment id' + currentEquipmentId);
        }

        const currentFavourites: Favourite[] = equipmentToFavouriteMap.get(currentEquipmentId);
        const currentTasks: Task[] = equipmentToTaskMap.get(currentEquipmentId);
        const equipmentGroup: EquipmentGroup = {
          equipment: currentEquipment,
          favourites: currentFavourites,
          tasks: currentTasks
        };
        equipmentGroups.push(equipmentGroup);
      });

      return {
        site: null,
        equipmentGroups: equipmentGroups,
        startDate: moment.utc(startDate),
        endDate: moment.utc(endDate),
        split: false
      };
    }));
  }

  parseFavIds(favIds: string | string[]): FavouriteId[] {
    if (favIds) {
      if (!Array.isArray(favIds)) {
        favIds = Array.of(favIds);
      }
    } else {
      favIds = [];
    }
    return favIds.map(f => Number(f));
  }

  parseTaskIds(taskIds: string | string[]): TaskId[] {
    if (taskIds) {
      if (!Array.isArray(taskIds)) {
        taskIds = Array.of(taskIds);
      }
    } else {
      taskIds = [];
    }

    return taskIds.map(t => {
      // old URLs have taskId-equipmentId
      if (t.indexOf('-') !== -1) {
        return Number(t.split('-')[0]);
      } else {
        return Number(t);
      }
    });
  }

  parseOldTaskIds(taskIds: string | string[]): string[] {
    if (taskIds) {
      if (!Array.isArray(taskIds)) {
        taskIds = Array.of(taskIds);
      }
    } else {
      taskIds = [];
    }
    return taskIds.filter(t => t.indexOf('-') !== -1);
  }

  getEquipmentIdForTask(currentTask: Task, availableEquipmentIds: EquipmentId[], oldTaskIds: string[]): EquipmentId {
    this.logger.debug('getEquipmentIdForTask() currentTask', currentTask);
    this.logger.debug('getEquipmentIdForTask() availableEquipmentIds', availableEquipmentIds);
    this.logger.debug('getEquipmentIdForTask() oldTaskIds', oldTaskIds);

    // get equipment id from old task id if available
    const currentOldTaskId: string = oldTaskIds.find(t => t.startsWith(currentTask.task_id + ''));

    this.logger.debug('getEquipmentIdForTask() currentOldTaskId', currentOldTaskId);
    if (currentOldTaskId !== undefined) {
      const oldTaskEquipmentId = Number(currentOldTaskId.split('-')[1]);
      this.logger.debug('getEquipmentIdForTask() oldTaskEquipmentId', oldTaskEquipmentId);

      // use old taskid-equipmentid if in available-equipmentids
      if (availableEquipmentIds.indexOf(oldTaskEquipmentId) !== -1) {
        this.logger.debug('getEquipmentIdForTask() oldTaskEquipmentId in availableEquipmentIds');
        return oldTaskEquipmentId;
      }
    }

    const taskEquipmentIds: number[] = this.getEquipmentIdsFromTaskName(currentTask);
    this.logger.debug('getEquipmentIdForTask() taskEquipmentIds', taskEquipmentIds);

    let equipmentId;
    if (taskEquipmentIds.length === 1) {
      equipmentId = taskEquipmentIds[0];
    } else {
      equipmentId = taskEquipmentIds.find(t => availableEquipmentIds.indexOf(t) !== -1);
    }
    this.logger.debug('getEquipmentIdForTask() equipmentId', equipmentId);

    // TODO maybe this could be smarter - call getTasksForEquipment(availableEquipemntIds) and see if any matches
    if (equipmentId === undefined) {
      this.logger.debug('getEquipmentIdForTask() cannot find equipmentId so picking first one');
      equipmentId = availableEquipmentIds[0];
    }

    return equipmentId;
  }

  getEquipmentIdsFromTaskName(task: Task): EquipmentId[] {
    let equipmentIds: EquipmentId[] = [];
    try {
      equipmentIds = task.task_name.split('~')[2].split(',').map(t => Number(t));
    } catch (e) {
    }

    this.logger.debug('getEquipmentIds() equipmentIds', equipmentIds);
    return equipmentIds;
  }

  createEquipmentIdToTaskMap(tasks: Task[], availableEquipmentIds: EquipmentId[], oldTaskIds: string[]): Map<EquipmentId, Task[]> {
    this.logger.debug('createEquipmentToTaskMap() tasks', tasks);
    this.logger.debug('createEquipmentToTaskMap() favailableEquipmentIds', availableEquipmentIds);
    this.logger.debug('createEquipmentToTaskMap() oldTaskIds', oldTaskIds);

    const equipmentTaskMap = new Map<EquipmentId, Task[]>();
    tasks.forEach(currentTask => {
      this.logger.debug('createEquipmentToTaskMap() currentTask', currentTask);

      const equipmentId: EquipmentId = this.getEquipmentIdForTask(currentTask, availableEquipmentIds, oldTaskIds);
      this.logger.debug('createEquipmentToTaskMap() equipmentId', equipmentId);

      const mapTasks: Task[] = equipmentTaskMap.get(equipmentId);
      if (mapTasks === undefined) {
        equipmentTaskMap.set(equipmentId, Array.of(currentTask));
      } else {
        mapTasks.push(currentTask);
        equipmentTaskMap.set(equipmentId, mapTasks);
      }
    });
    this.logger.debug('createEquipmentIdToTaskMap() equipmentTaskMap', equipmentTaskMap);
    return equipmentTaskMap;
  }

  createEquipmentIdToFavouriteMap(favourites: Favourite[]): Map<EquipmentId, Favourite[]> {
    this.logger.debug('createEquipmentToFavouriteMap() favourites', favourites);
    const equipmentMap = new Map<EquipmentId, Favourite[]>();
    favourites.forEach(currentFavourite => {
      const equipmentId = currentFavourite.equipment.equipment_id;
      const mapFavourites: Favourite[] = equipmentMap.get(equipmentId);
      if (mapFavourites === undefined) {
        equipmentMap.set(equipmentId, Array.of(currentFavourite));
      } else {
        mapFavourites.push(currentFavourite);
        equipmentMap.set(equipmentId, mapFavourites);
      }
    });
    this.logger.debug('createEquipmentToFavouriteMap() equipmentMap', equipmentMap);
    return equipmentMap;
  }

  /*
    fav-<EID>=<FID1>,<FID2>,<FID3>
    fav-<EID>-functions=<FUN1>,<FUN2>,<FUN3>
    fav-<EID>-<FUN1>-duration=<FUN1_DURATION>
    fav-<EID>-<FUN2>-duration=<FUN2_DURATION>
    fav-<EID>-<FUN3>-duration=<FUN3_DURATION>
  */

  getFunctionParamsFromUrl(equipmentId: EquipmentId, queryParamMap: ParamMap): FunctionParamsAndChartRawValues {
    const params: FunctionParams[] = [];
    const functionNamesParam: string = queryParamMap.get(this.FAVOURITE_URL_PARAM_PREFIX + equipmentId + '-functions');
    let chartRawValues = false;

    if (functionNamesParam && functionNamesParam.trim().length > 0) {
      const functionNames: string[] = functionNamesParam.split(',');
      functionNames.forEach(functionName => {
        chartRawValues = functionName.toUpperCase() === 'RAW';
        const timePeriodKey = this.FAVOURITE_URL_PARAM_PREFIX + equipmentId + '-' + functionName + '-duration';
        const timePeriod: TimePeriod = TimePeriod.fromHashCode(queryParamMap.get(timePeriodKey));
        if (functionName.toUpperCase() !== 'RAW') {
          params.push(<FunctionParams>{
            functionName: functionName,
            timePeriod: timePeriod
          });
        }
      });
    }
    return {functionParams: params, chartRawValues: chartRawValues};
  }
}
